import { useState } from 'react';
import { Mail, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage, type Locale } from '@/hooks/useLanguage';
import { isOmnisendEnabled } from '@/lib/appSettings';

type Copy = {
  badge: string;
  title: string;
  subtitle: string;
  placeholder: string;
  cta: string;
  ctaLoading: string;
  success: string;
  errorInvalid: string;
  errorGeneric: string;
  privacy: string;
};

const COPY: Record<Locale, Copy> = {
  de: {
    badge: '15% Rabatt auf deine erste Bestellung',
    title: 'Hol dir Tipps für Social-Media-Wachstum',
    subtitle: 'Melde dich zum Newsletter an und erhalte exklusive Tipps, Strategien und einen 15% Rabattcode für deine erste Bestellung.',
    placeholder: 'Deine E-Mail-Adresse',
    cta: 'Jetzt anmelden',
    ctaLoading: 'Wird gesendet…',
    success: 'Danke! Schau in dein Postfach – dein 15% Rabattcode ist unterwegs.',
    errorInvalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
    errorGeneric: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
    privacy: 'Kein Spam. Abmeldung jederzeit möglich.',
  },
  en: {
    badge: '15% off your first order',
    title: 'Get tips for social media growth',
    subtitle: 'Sign up for our newsletter and receive exclusive tips, strategies and a 15% discount code for your first order.',
    placeholder: 'Your email address',
    cta: 'Sign up now',
    ctaLoading: 'Sending…',
    success: 'Thank you! Check your inbox – your 15% discount code is on its way.',
    errorInvalid: 'Please enter a valid email address.',
    errorGeneric: 'Something went wrong. Please try again.',
    privacy: 'No spam. Unsubscribe anytime.',
  },
  it: {
    badge: '15% di sconto sul tuo primo ordine',
    title: 'Ricevi consigli per la crescita sui social media',
    subtitle: 'Iscriviti alla newsletter e ricevi consigli esclusivi, strategie e un codice sconto del 15% sul tuo primo ordine.',
    placeholder: 'Il tuo indirizzo e-mail',
    cta: 'Iscriviti ora',
    ctaLoading: 'Invio in corso…',
    success: 'Grazie! Controlla la tua casella di posta – il tuo codice sconto del 15% è in arrivo.',
    errorInvalid: 'Inserisci un indirizzo e-mail valido.',
    errorGeneric: 'Qualcosa è andato storto. Riprova.',
    privacy: 'Niente spam. Disiscrizione in qualsiasi momento.',
  },
  fr: {
    badge: '15% de réduction sur ta première commande',
    title: 'Reçois des conseils pour ta croissance sur les réseaux sociaux',
    subtitle: 'Inscris-toi à la newsletter et reçois des conseils exclusifs, des stratégies et un code de réduction de 15% pour ta première commande.',
    placeholder: 'Ton adresse e-mail',
    cta: 'S\'inscrire',
    ctaLoading: 'Envoi en cours…',
    success: 'Merci ! Vérifie ta boîte de réception – ton code de réduction de 15% arrive.',
    errorInvalid: 'Merci de saisir une adresse e-mail valide.',
    errorGeneric: 'Une erreur est survenue. Merci de réessayer.',
    privacy: 'Pas de spam. Désinscription à tout moment.',
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewsletterSection() {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.de;
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setError(c.errorInvalid);
      setState('error');
      return;
    }
    setState('loading');
    try {
      // Storefront aus window.location.host ableiten (AT-Signups -> AT-Mails).
      const storefront = typeof window !== 'undefined' && window.location.host.includes('bigfollow.at') ? 'at' : 'ch';

      // Primaerer Pfad: unser eigener Welcome-Endpoint. MUSS awaiten,
      // damit ein Fehler den User sichtbar erreicht (kein stiller Verlust,
      // wenn Omnisend abgeschaltet ist).
      const { error: signupError } = await supabase.functions.invoke('newsletter-signup', {
        body: { email: trimmed, storefront, locale, source: 'footer-form' },
      });
      if (signupError) throw signupError;

      // Sekundaerer Pfad: Omnisend-Push nur, wenn Integration aktiv ist.
      // Fire-and-forget: darf den Erfolg der Anmeldung niemals blockieren.
      isOmnisendEnabled().then((enabled) => {
        if (!enabled) return;
        supabase.functions.invoke('omnisend-sync', {
          body: {
            action: 'sync_contact',
            contact: {
              email: trimmed,
              tags: ['Newsletter', 'newsletter-signup', `lang:${locale}`, 'footer-form'],
            },
          },
        }).catch((err) => console.warn('[omnisend] sync_contact failed', err));
      }).catch(() => { /* ignore */ });

      setState('success');
      setEmail('');
    } catch (err) {
      console.error('[newsletter] sign-up failed', err);
      setError(c.errorGeneric);
      setState('error');
    }
  }

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(242,55%,30%)] via-[hsl(245,60%,38%)] to-[hsl(265,65%,45%)] text-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(circle at 15% 20%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 85% 80%, rgba(255,255,255,0.12), transparent 50%)',
        }}
      />
      <div className="container relative py-12 md:py-16">
        <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide backdrop-blur-sm sm:text-sm">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            {c.badge}
          </span>
          <h2 className="mt-4 text-2xl font-bold leading-tight sm:text-3xl md:text-4xl">
            {c.title}
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-white/85 sm:text-base">
            {c.subtitle}
          </p>

          {state === 'success' ? (
            <div className="mt-6 flex items-center gap-2 rounded-xl bg-white/15 px-5 py-4 text-sm font-medium backdrop-blur-sm sm:text-base">
              <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span>{c.success}</span>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="mt-6 flex w-full max-w-xl flex-col gap-2 sm:flex-row sm:gap-2"
              noValidate
            >
              <label htmlFor="newsletter-email" className="sr-only">
                {c.placeholder}
              </label>
              <div className="relative flex-1">
                <Mail
                  className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[hsl(242,45%,25%)]/60"
                  aria-hidden="true"
                />
                <input
                  id="newsletter-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={c.placeholder}
                  required
                  disabled={state === 'loading'}
                  className="h-12 w-full rounded-xl border-0 bg-white pl-10 pr-3 text-[16px] text-[hsl(242,45%,15%)] shadow-sm outline-none ring-2 ring-transparent placeholder:text-[hsl(242,45%,25%)]/50 focus:ring-white/60 disabled:opacity-60"
                />
              </div>
              <button
                type="submit"
                disabled={state === 'loading'}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-white px-6 text-sm font-semibold text-[hsl(242,55%,30%)] shadow-sm transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
              >
                {state === 'loading' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {c.ctaLoading}
                  </>
                ) : (
                  c.cta
                )}
              </button>
            </form>
          )}

          {state === 'error' && error && (
            <p className="mt-3 text-sm text-red-100" role="alert">
              {error}
            </p>
          )}

          <p className="mt-3 text-xs text-white/65">{c.privacy}</p>
        </div>
      </div>
    </section>
  );
}